(() => {
  const root = document.getElementById('LkPersonalChatInvite');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  if (/\/(?:checkouts?|orders|account|challenge|password)(?:\/|$)/.test(location.pathname)) return;

  const storageKey = 'lk:personal-chat:v1';
  const sessionLifetime = 30 * 60 * 1000;
  const minimumGap = 5 * 60 * 1000;
  const displayDuration = 45 * 1000;
  const cartDelay = (Number(root.dataset.cartDelay) || 3) * 60000;
  const browseDelay = (Number(root.dataset.browseDelay) || 12) * 60000;
  const requestedPreview = new URLSearchParams(location.search).get('lk_chat_preview');
  const preview = root.dataset.preview === 'true' && ['cart', 'browse'].includes(requestedPreview) ? requestedPreview : '';
  const title = root.querySelector('[data-lk-chat-title]');
  const message = root.querySelector('[data-lk-chat-message]');
  const fallback = root.querySelector('[data-lk-chat-fallback]');
  const startButton = root.querySelector('[data-lk-chat-start]');
  const observedRoots = new WeakSet();
  let chatHost;
  let activator;
  let stylesPromise;
  let pending = false;
  let currentVariant = '';
  let visibleSince = 0;
  let timer;
  let lastTick = performance.now();
  let lastSave = 0;
  let refreshPromise;
  let refreshTimer;
  let lastCartCheck = -Infinity;
  let lastCartAttempt = -Infinity;

  const freshState = () => ({
    version: 1,
    lastAt: Date.now(),
    activeMs: 0,
    cartMs: 0,
    cartCount: 0,
    shown: [],
    lastInviteMs: -minimumGap,
    suppressed: false
  });

  const readState = () => {
    if (preview) return freshState();
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey));
      if (!saved || saved.version !== 1 || Date.now() - saved.lastAt >= sessionLifetime) return freshState();
      return {
        version: 1,
        lastAt: Number(saved.lastAt) || Date.now(),
        activeMs: Math.max(0, Number(saved.activeMs) || 0),
        cartMs: Math.max(0, Number(saved.cartMs) || 0),
        cartCount: Math.max(0, Number(saved.cartCount) || 0),
        shown: Array.isArray(saved.shown) ? [...new Set(saved.shown.filter(value => ['cart', 'browse'].includes(value)))] : [],
        lastInviteMs: Number.isFinite(saved.lastInviteMs) ? saved.lastInviteMs : -minimumGap,
        suppressed: saved.suppressed === true
      };
    } catch {
      return freshState();
    }
  };

  let state = readState();

  const persist = () => {
    state.lastAt = Date.now();
    lastSave = performance.now();
    if (preview) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch {}
  };

  const track = (action, variant = currentVariant) => {
    if (preview) return;
    document.dispatchEvent(new CustomEvent('lk:chat-invite', { detail: { action, variant } }));
    try {
      if (window.lkShopifyConsent?.analyticsAllowed()) {
        window.dataLayer?.push({ event: `lk_chat_invite_${action}`, chat_invite_variant: variant });
      }
    } catch {}
  };

  const visibleRect = element => {
    if (!element || !element.isConnected) return null;
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' ? rect : null;
  };

  const hide = () => {
    root.hidden = true;
    currentVariant = '';
  };

  const suppress = reason => {
    if (state.suppressed) { hide(); return; }
    state.suppressed = true;
    track(reason);
    hide();
    persist();
    clearInterval(timer);
    chatObserver.disconnect();
  };

  const chatObserver = new MutationObserver(() => {
    if (activator?.getAttribute('aria-expanded') === 'true') suppress('chat_open');
  });

  const inspectChat = () => {
    const host = document.querySelector('shopify-chat');
    if (!host) return null;
    if (host !== chatHost) {
      chatHost = host;
      activator = null;
      host.addEventListener('click', event => {
        if (event.composedPath().some(node => node?.getAttribute?.('part')?.split(' ').includes('activator'))) suppress('chat_open');
      });
    }
    if (!activator?.isConnected) {
      const visit = element => {
        const shadow = element.shadowRoot;
        if (!shadow) return;
        if (!observedRoots.has(shadow) && !state.suppressed) {
          observedRoots.add(shadow);
          chatObserver.observe(shadow, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
        }
        activator ||= shadow.querySelector('button[part~="activator"]');
        if (!activator) shadow.querySelectorAll('*').forEach(child => { if (!activator && child.shadowRoot) visit(child); });
      };
      activator = null;
      visit(host);
    }
    if (activator?.getAttribute('aria-expanded') === 'true') suppress('chat_open');
    return activator && visibleRect(activator) ? activator : null;
  };

  const blocked = () => {
    if (document.hidden) return true;
    const focused = document.activeElement;
    if (focused && !root.contains(focused) && focused.matches('input, textarea, select, [contenteditable="true"]')) return true;
    const overlays = document.querySelectorAll('dialog[open], [aria-modal="true"]:not([hidden]), .site-header__cart.is-active, .site-header__drawer.is-active, .modal--is-active, #shopify-pc__banner, #shopify-pc__prefs, #CybotCookiebotDialog');
    return [...overlays].some(element => !root.contains(element) && visibleRect(element));
  };

  const position = button => {
    const anchor = visibleRect(button);
    if (!anchor) return false;
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : innerHeight;
    let bottomEdge = Math.min(viewportBottom - 16, anchor.top - 12);
    for (const sticky of document.querySelectorAll('.lk-sticky-cart.is-visible, [data-lk-sticky-cart].is-visible')) {
      const rect = visibleRect(sticky);
      if (rect && rect.top < viewportBottom && rect.bottom > 0) bottomEdge = Math.min(bottomEdge, rect.top - 12);
    }
    const topEdge = (viewport?.offsetTop || 0) + 16;
    if (bottomEdge - topEdge < 240) return false;
    root.style.maxHeight = `${Math.floor(bottomEdge - topEdge)}px`;
    root.style.setProperty('--lk-invite-bottom', `${Math.max(16, innerHeight - bottomEdge)}px`);
    const leftAligned = anchor.left + anchor.width / 2 < innerWidth / 2;
    const anchorEdge = leftAligned ? anchor.left : innerWidth - anchor.right;
    const cardWidth = Math.min(348, innerWidth - 32);
    const edge = Math.min(Math.max(16, anchorEdge), Math.max(16, innerWidth - cardWidth - 16));
    root.style.setProperty('--lk-invite-left', leftAligned ? `${edge}px` : 'auto');
    root.style.setProperty('--lk-invite-right', leftAligned ? 'auto' : `${edge}px`);
    return true;
  };

  const updateCart = value => {
    const count = Number(value);
    if (!Number.isFinite(count) || count < 0) return;
    if (!count || !state.cartCount) state.cartMs = 0;
    state.cartCount = count;
    if (!count && currentVariant === 'cart') hide();
    persist();
  };

  const refreshCart = () => {
    if (refreshPromise) return refreshPromise;
    lastCartAttempt = performance.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    const path = window.Shopify?.routes?.root || '/';
    refreshPromise = fetch(`${path}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error('cart'); return response.json(); })
      .then(cart => { lastCartCheck = performance.now(); updateCart(cart.item_count); return true; })
      .catch(() => false)
      .finally(() => { clearTimeout(timeout); refreshPromise = null; });
    return refreshPromise;
  };

  const ensureStyles = () => {
    if (!stylesPromise) stylesPromise = new Promise(resolve => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = root.dataset.stylesheet;
      link.onload = () => resolve(true);
      link.onerror = () => resolve(false);
      document.head.append(link);
    });
    return stylesPromise;
  };

  const candidate = () => {
    if (state.suppressed || state.shown.length >= 2 || state.activeMs - state.lastInviteMs < minimumGap) return '';
    if (preview) return state.shown.includes(preview) ? '' : preview;
    if (state.cartCount > 0 && !state.shown.includes('cart')) return state.cartMs >= cartDelay ? 'cart' : '';
    return root.dataset.browseEnabled === 'true' && !state.shown.includes('browse') && state.activeMs >= browseDelay ? 'browse' : '';
  };

  const show = async variant => {
    pending = true;
    try {
      if (!preview && variant === 'cart' && performance.now() - lastCartCheck > 5000) {
        if (performance.now() - lastCartAttempt < 30000 || !(await refreshCart())) return;
      }
      if (!(await ensureStyles())) return;
      const button = inspectChat();
      if (candidate() !== variant || !button || blocked() || !position(button)) return;
      title.textContent = root.dataset[`${variant}Title`];
      message.textContent = root.dataset[`${variant}Message`];
      fallback.hidden = true;
      currentVariant = variant;
      visibleSince = state.activeMs;
      root.hidden = false;
      state.shown.push(variant);
      state.lastInviteMs = state.activeMs;
      persist();
      track('view');
    } finally {
      pending = false;
    }
  };

  const advance = () => {
    const now = performance.now();
    if (!document.hidden && !state.suppressed) {
      const elapsed = Math.max(0, Math.min(now - lastTick, 2000));
      state.activeMs += elapsed;
      if (state.cartCount > 0) state.cartMs += elapsed;
    }
    lastTick = now;
  };

  const tick = () => {
    advance();
    if (document.hidden) { hide(); return; }
    if (state.suppressed) return;
    if (performance.now() - lastSave >= 5000) persist();
    const button = inspectChat();
    if (!button || blocked()) { hide(); return; }
    if (currentVariant) {
      if (!position(button) || (!preview && state.activeMs - visibleSince >= displayDuration && !root.contains(document.activeElement))) hide();
      return;
    }
    const next = candidate();
    if (next && !pending) void show(next);
  };

  updateCart(root.dataset.cartCount);
  root.querySelector('[data-lk-chat-dismiss]').addEventListener('click', () => {
    const shouldRestoreFocus = root.contains(document.activeElement);
    suppress('dismiss');
    if (shouldRestoreFocus) activator?.focus({ preventScroll: true });
  });
  root.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      suppress('dismiss');
      activator?.focus({ preventScroll: true });
    }
  });
  startButton.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    const button = inspectChat();
    if (!button) { fallback.hidden = false; return; }
    const variant = currentVariant;
    suppress('start');
    if (button.getAttribute('aria-expanded') !== 'true') button.click();
    if (button.getAttribute('aria-expanded') === 'true') return;
    setTimeout(() => {
      if (button.getAttribute('aria-expanded') === 'true') return;
      currentVariant = variant;
      fallback.hidden = false;
      root.hidden = false;
    }, 1500);
  });

  const requestCartRefresh = () => {
    if (state.suppressed) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { void refreshCart(); }, 200);
  };
  document.addEventListener('ajaxProduct:added', requestCartRefresh);
  document.addEventListener('cart:updated', event => {
    if (event.detail?.cart?.item_count !== undefined) updateCart(event.detail.cart.item_count);
    else requestCartRefresh();
  });
  document.addEventListener('lk:cart-rendered', () => {
    const items = document.querySelector('.cart__items[data-count]');
    if (items) updateCart(items.dataset.count);
    else requestCartRefresh();
  });
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    let url;
    try { url = new URL(link.href, location.origin); } catch { return; }
    if (url.origin !== location.origin || !/\/(?:checkouts?)(?:\/|$)/.test(url.pathname)) return;
    queueMicrotask(() => { if (!event.defaultPrevented) suppress('checkout'); });
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (submitter?.name !== 'checkout' && !/\/(?:checkouts?)(?:\/|$)/.test(new URL(form.action, location.origin).pathname)) return;
    queueMicrotask(() => { if (!event.defaultPrevented) suppress('checkout'); });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { advance(); hide(); persist(); }
    else {
      if (Date.now() - state.lastAt >= sessionLifetime) { const count = state.cartCount; state = freshState(); updateCart(count); }
      lastTick = performance.now();
      if (!state.suppressed) { clearInterval(timer); timer = setInterval(tick, 1000); void refreshCart(); }
    }
  });
  window.addEventListener('pagehide', () => { advance(); persist(); });
  window.addEventListener('pageshow', event => {
    lastTick = performance.now();
    if (event.persisted && !state.suppressed) void refreshCart();
  });
  window.addEventListener('resize', () => { if (currentVariant && activator && !position(activator)) hide(); }, { passive: true });
  window.visualViewport?.addEventListener('resize', () => { if (currentVariant && activator && !position(activator)) hide(); }, { passive: true });
  if (!state.suppressed) { timer = setInterval(tick, 1000); tick(); }
})();
